import { setMinimalPromiseTime } from 'common/utils/setMinimalPromiseTime';
import {
  fetchSimInventoryPagination,
  fetchSimInventoryWithConfiguration,
} from 'simInventory/SimInventoryApi/simInventoryApi';
import { Sorting } from 'src/common/components/Filters/constants/sorting';
import { SimInventoryFiltersV2 } from 'simInventory/models/SimProfile.model';
import { simInventoryQueries } from 'simInventory/Inventory/queries/simInventoryQueries';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { SimListResponseData } from 'src/common/components/Sim/Inventory/types/simListTypes';
import { useValidatedSearchId } from 'src/common/components/Filters/hooks/useValidatedSearchId';
import { getSearchIds } from 'src/common/components/Sim/Filters/SimIdSearchInput.util';

export const useSimInventoryFetch = (
  filters: SimInventoryFiltersV2,
  sort: { columnName: string; sort: Sorting } | null,
  rowsPerPage: number,
  page: number,
) => {
  const { tags, searchText, chosenIdType, customFields, ...otherFilters } = filters;

  const { search } = useValidatedSearchId(searchText);

  const queryKey = simInventoryQueries.list({
    searchText: search,
    chosenIdType,
    page,
    rowsPerPage,
    ...otherFilters,
    tags,
    sorting: sort,
    customFields,
  });

  const { data: simInventory, isRefetching } = useSuspenseQuery<SimListResponseData>({
    queryKey: queryKey,
    queryFn: ({ signal }) => {
      return setMinimalPromiseTime(
        fetchSimInventoryWithConfiguration(
          page,
          rowsPerPage,
          {
            searchText: getSearchIds(search),
            chosenIdType: chosenIdType,
            tags,
            customFields,
            ...otherFilters,
          },
          sort,
          true,
          signal,
        ),
      );
    },
    refetchOnWindowFocus: false,
  });

  const { status, data: pagination } = useQuery({
    queryKey: simInventoryQueries.pagination({
      searchText: search,
      chosenIdType,
      ...otherFilters,
      tags,
      sorting: sort,
      customFields,
    }),
    queryFn: ({ signal }) =>
      fetchSimInventoryPagination(
        page,
        rowsPerPage,
        {
          searchText: getSearchIds(search),
          chosenIdType: chosenIdType,
          tags,
          customFields,
          ...otherFilters,
        },
        signal,
      ),
  });

  return {
    simInventory,
    pagination: {
      totalNumberOfItems: status === 'success' ? pagination?.totalNumberOfItems : -1,
    },
    queryKey,
    isRefetching,
  };
};
